import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { api } from "@/lib/api";
import AdaptiveAudio from "@/components/game/AdaptiveAudio";
import BountyBoard from "@/components/game/BountyBoard";
import { Trophy, Ghost, Loader2, Eye, Users } from "lucide-react";
import { toast } from "sonner";

const MENU_TRACK = "https://incompetech.com/music/royalty-free/mp3-royaltyfree/Skye%20Cuillin.mp3";

export default function LoginPage() {
  const navigate = useNavigate();
  const [methods, setMethods] = useState({ google: true, discord: false, guest: true });
  const [guestBusy, setGuestBusy] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        await api.get("/auth/me");
        navigate("/dashboard", { replace: true });
      } catch { /* not authed */ }
    })();
    (async () => {
      try {
        const m = await api.get("/auth/methods");
        if (m && typeof m === "object") setMethods((prev) => ({ ...prev, ...m }));
      } catch { /* keep defaults */ }
    })();
  }, [navigate]);

  const handleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const handleDiscord = () => {
    window.location.href = `${process.env.REACT_APP_BACKEND_URL}/api/auth/discord/login?next=/dashboard`;
  };

  const handleGuest = async () => {
    if (guestBusy) return;
    setGuestBusy(true);
    try {
      const res = await api.post("/auth/guest", {});
      if (res?.user?.name) {
        toast.success(`Welcome, ${res.user.name} — your ember flickers.`);
        navigate("/dashboard", { replace: true });
      } else {
        throw new Error("Guest signup returned no user");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not enter as a wanderer.");
      setGuestBusy(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-6 relative" data-testid="login-page">
      <div className="absolute top-6 right-6 z-10 flex items-center gap-2">
        <Link
          to="/party"
          data-testid="login-party-link"
          className="panel px-3 py-2 flex items-center gap-2 hover:border-[#d4af37] transition-colors"
        >
          <Users size={14} className="gold-text" />
          <span className="text-[10px] tracking-[0.3em] ash-text">CO-OP</span>
        </Link>
        <Link
          to="/spectate"
          data-testid="login-spectate-link"
          className="panel px-3 py-2 flex items-center gap-2 hover:border-[#d4af37] transition-colors"
        >
          <Eye size={14} className="blood-text" />
          <span className="text-[10px] tracking-[0.3em] ash-text">WATCH LIVE</span>
        </Link>
        <Link
          to="/hall"
          data-testid="login-hall-link"
          className="panel px-3 py-2 flex items-center gap-2 hover:border-[#d4af37] transition-colors"
        >
          <Trophy size={14} className="gold-text" />
          <span className="text-[10px] tracking-[0.3em] ash-text">HALL OF ASHES</span>
        </Link>
        <AdaptiveAudio trackUrl={MENU_TRACK} />
      </div>
      <div className="max-w-2xl w-full">
        <div className="text-center fade-up">
          <p className="ash-text tracking-[0.4em] text-xs mb-4">A TACTICAL CO-OP DUNGEON CRAWLER</p>
          <h1 className="font-heading text-5xl sm:text-6xl lg:text-7xl leading-tight text-[#f0e6d2]">
            <span className="gold-text italic">Champions</span>
            <span className="block text-3xl sm:text-4xl lg:text-5xl ash-text italic font-narrative mt-2">of the</span>
            <span className="gold-text italic">Dawn Vanguard</span>
          </h1>
          <div className="mt-4 h-px bg-gradient-to-r from-transparent via-[#d4af37]/40 to-transparent" />
          <p className="font-narrative text-lg md:text-xl mt-6 ash-text italic max-w-lg mx-auto">
            The <span className="text-[#f0e6d2] not-italic">Ember King</span> tore the world's heart from its root
            and buried it beneath his black keep. Twenty floors down, the Ember still whispers —
            calling for a Vanguard.
          </p>
          <p className="font-narrative text-base mt-3 gold-text italic max-w-md mx-auto">
            Three lives a day. Twenty floors. Four bosses. One dawn.
          </p>
        </div>

        <div className="mt-14 flex flex-col items-center gap-4 fade-up delay-2">
          <button
            data-testid="google-login-btn"
            onClick={handleLogin}
            className="btn-primary"
          >
            ⚔  Sign in with Google  ⚔
          </button>

          {methods.discord && (
            <button
              data-testid="discord-login-btn"
              onClick={handleDiscord}
              className="btn-glow flex items-center gap-2 px-6 py-3"
              style={{ borderColor: "#5865F2", background: "linear-gradient(180deg, #1a1c3d, #0a0b1f)" }}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="#5865F2" aria-hidden="true">
                <path d="M20.317 4.37a19.79 19.79 0 0 0-4.885-1.515.074.074 0 0 0-.079.037c-.21.375-.444.865-.608 1.25a18.27 18.27 0 0 0-5.487 0 12.64 12.64 0 0 0-.617-1.25.077.077 0 0 0-.079-.037A19.736 19.736 0 0 0 3.677 4.37a.07.07 0 0 0-.032.027C.533 9.046-.32 13.58.099 18.057a.082.082 0 0 0 .031.057 19.9 19.9 0 0 0 5.993 3.03.078.078 0 0 0 .084-.028c.462-.63.874-1.295 1.226-1.994a.076.076 0 0 0-.041-.106 13.107 13.107 0 0 1-1.872-.892.077.077 0 0 1-.008-.128 10.2 10.2 0 0 0 .372-.292.074.074 0 0 1 .077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 0 1 .078.01c.12.098.246.198.373.292a.077.077 0 0 1-.006.127 12.299 12.299 0 0 1-1.873.892.077.077 0 0 0-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 0 0 .084.028 19.839 19.839 0 0 0 6.002-3.03.077.077 0 0 0 .032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 0 0-.031-.03zM8.02 15.331c-1.182 0-2.156-1.085-2.156-2.419 0-1.333.955-2.418 2.156-2.418 1.211 0 2.175 1.094 2.156 2.418 0 1.334-.955 2.42-2.156 2.42zm7.974 0c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.955-2.418 2.157-2.418 1.21 0 2.174 1.094 2.156 2.418 0 1.334-.946 2.42-2.156 2.42z"/>
              </svg>
              <span>Continue with Discord</span>
            </button>
          )}

          {methods.guest && (
            <button
              data-testid="guest-login-btn"
              onClick={handleGuest}
              disabled={guestBusy}
              className="btn-glow flex items-center gap-2 px-6 py-3 disabled:opacity-50"
            >
              {guestBusy
                ? <><Loader2 size={14} className="animate-spin" /> ENTERING THE ASHES...</>
                : <><Ghost size={14} className="ash-text" /> <span>Play as Guest</span></>}
            </button>
          )}

          <p className="text-xs ash-text tracking-widest">
            {methods.guest
              ? "GUEST PROGRESS SAVES TO THIS BROWSER ONLY · CLAIM AN ACCOUNT TO SECURE IT"
              : "SESSION SECURED BY EMERGENT AUTH"}
          </p>
        </div>

        <div className="mt-16 max-w-lg mx-auto fade-up delay-3">
          <BountyBoard />
        </div>
      </div>
    </div>
  );
}
